import React, { useState } from "react";
import { FaWhatsapp, FaPhoneAlt, FaShoppingCart, FaLeaf, FaWater, FaSeedling, FaShieldAlt, FaExpand, FaHome } from "react-icons/fa";

// Mock image - replace with actual image path
import cocodiscpng from "../../assets/products/coins12.png";

const CocopeatCoins = () => {
  const [showOptions, setShowOptions] = useState(false);
  const [activeTab, setActiveTab] = useState('description');

  const whatsappNumber = "+919492228585";
  const whatsappMessage = encodeURIComponent(
    "Hello Mr.Ramana Sir! I'm interested in ordering the Cocopeat Coins. Could you please provide more details?"
  );

  const handleOrderClick = () => {
    setShowOptions(!showOptions);
  };

  const benefits = [
    { icon: <FaLeaf />, title: "100% Organic", desc: "Natural and eco-friendly growing medium" },
    { icon: <FaWater />, title: "Water Retention", desc: "Holds 1.5L water for optimal plant growth" },
    { icon: <FaExpand />, title: "Expands 1L", desc: "Compact disc expands to 1 liter when hydrated" },
    { icon: <FaShieldAlt />, title: "Pathogen-Free", desc: "Safe and healthy growing environment" },
    { icon: <FaSeedling />, title: "Perfect pH", desc: "Ideal pH range of 5.5-6.5 for plants" },
    { icon: <FaHome />, title: "Home Gardening", desc: "Perfect for small-scale home gardening" }
  ];

  const specifications = [
    { param: "Dimensions", value: "8 cm x 8 cm x 2 cm" },
    { param: "Material", value: "100% Coco Peat" },
    { param: "Weight", value: "100 g (per disc)" },
    { param: "Expansion", value: "Up to 1 L (per disc)" },
    { param: "Water Holding", value: "Around 1.5 L" },
    { param: "EC Level", value: "EC 0.2 to EC 1.0 mS/cm" },
    { param: "pH Value", value: "5.5 - 6.5" }
  ];

  return (
    <div className="min-h-screen bg-gray-50 shadow-md my-10">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-green-50 to-green-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Product Image */}
            <div className="order-1 lg:order-1">
              <div className="relative bg-white rounded-2xl shadow-xl p-6 lg:p-8">
                <div className="absolute top-4 right-4 bg-green-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                  Coins
                </div>
                <img
                  src={cocodiscpng}
                  alt="Cocopeat Coins"
                  className="w-full h-64 sm:h-80 lg:h-96 object-contain rounded-lg"
                />
              </div>
            </div>
            
            {/* Product Info */}
            <div className="order-2 lg:order-2 space-y-6">
              <div>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                  Cocopeat Coins
                </h1>
                <p className="text-lg sm:text-xl text-gray-600 leading-relaxed">
                  Compact and convenient coco peat coins perfect for home gardening. These lightweight discs expand when hydrated, providing excellent water retention and aeration for all your plants.
                </p>
              </div>
              
              <div className="flex flex-wrap gap-2">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                  <FaLeaf className="mr-1" /> 100% Organic
                </span>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  <FaWater className="mr-1" /> High Water Retention
                </span>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                  <FaShieldAlt className="mr-1" /> Pathogen-Free
                </span>
              </div>
              
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Key Features</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex items-center space-x-2">
                    <FaExpand className="text-green-600" />
                    <span className="text-gray-700">Expands to 1L per disc</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <FaWater className="text-blue-600" />
                    <span className="text-gray-700">1.5L Water Holding</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <FaSeedling className="text-green-600" />
                    <span className="text-gray-700">pH 5.5-6.5</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <FaHome className="text-purple-600" />
                    <span className="text-gray-700">Home Gardening</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Product Details Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Tabs */}
        <div className="mb-8">
          <div className="flex flex-wrap gap-2 sm:gap-4 border-b border-gray-200">
            {['description', 'specifications', 'benefits'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 text-sm sm:text-base font-medium capitalize transition-colors duration-200 border-b-2 ${
                  activeTab === tab
                    ? 'text-green-600 border-green-600'
                    : 'text-gray-500 border-transparent hover:text-green-600 hover:border-green-300'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-xl shadow-lg p-6 lg:p-8">
          {activeTab === 'description' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-900">Product Description</h2>
              <p className="text-gray-700 leading-relaxed text-lg">
                Tirumala Cocopeat Coins are compact, lightweight, and designed for convenience. These coins expand when hydrated, providing excellent water retention and aeration for flowers, vegetables, and ornamental plants. Completely organic and eco-friendly, they ensure a clean and healthy environment for your plants.
              </p>
              <div className="bg-green-50 border-l-4 border-green-400 p-4">
                <p className="text-green-800 font-medium">
                  💡 Pro Tip: Soak each disc in water for 10-15 minutes to achieve full expansion and optimal texture for your plants.
                </p>
              </div>
            </div>
          )}

          {activeTab === 'specifications' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-900">Technical Specifications</h2>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse bg-white">
                  <thead>
                    <tr className="bg-green-50">
                      <th className="border border-gray-300 p-4 text-left font-semibold text-gray-900">Parameter</th>
                      <th className="border border-gray-300 p-4 text-left font-semibold text-gray-900">Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {specifications.map((spec, index) => (
                      <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                        <td className="border border-gray-300 p-4 font-medium text-gray-700">{spec.param}</td>
                        <td className="border border-gray-300 p-4 text-gray-700">{spec.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'benefits' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-900">Key Benefits</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {benefits.map((benefit, index) => (
                  <div key={index} className="bg-gray-50 rounded-xl p-6 text-center hover:shadow-md transition-shadow duration-200">
                    <div className="text-3xl text-green-600 mb-3 flex justify-center">{benefit.icon}</div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                    <p className="text-gray-600">{benefit.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-green-600 to-green-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center space-y-6">
            <h2 className="text-3xl sm:text-4xl font-bold">Perfect for Your Home Garden</h2>
            <p className="text-xl text-green-100 max-w-2xl mx-auto">
              Get your premium cocopeat coins today and give your plants the perfect growing medium they deserve.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href={`https://wa.me/${whatsappNumber}?text=${whatsappMessage}`}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white text-green-600 px-8 py-3 rounded-full font-semibold text-lg hover:bg-green-50 transition-all duration-300 flex items-center space-x-2 hover:scale-105"
              >
                <FaWhatsapp className="text-xl" />
                <span>Order via WhatsApp</span>
              </a>
              <a
                href={`tel:${whatsappNumber}`}
                className="bg-green-800 text-white px-8 py-3 rounded-full font-semibold text-lg hover:bg-green-900 transition-all duration-300 flex items-center space-x-2 hover:scale-105"
              >
                <FaPhoneAlt className="text-xl" />
                <span>Call Now</span>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Floating Action Button */}
      <div
        onClick={handleOrderClick}
        className="fixed bottom-6 right-6 bg-green-600 text-white p-4 rounded-full shadow-xl hover:bg-green-700 transition-all duration-300 cursor-pointer hover:scale-110 z-50"
      >
        <FaShoppingCart className="text-xl" />
      </div>

      {/* Floating Options Menu */}
      <div
        className={`fixed bottom-24 right-6 transform transition-all duration-300 ease-in-out z-40 ${
          showOptions
            ? "opacity-100 translate-y-0"
            : "opacity-0 translate-y-10 pointer-events-none"
        }`}
      >
        <div className="bg-white shadow-xl rounded-xl p-4 space-y-3 border">
          <a
            href={`https://wa.me/${whatsappNumber}?text=${whatsappMessage}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center space-x-3 bg-green-600 text-white px-4 py-3 rounded-lg hover:bg-green-700 transition-all duration-300 hover:scale-105"
          >
            <FaWhatsapp className="text-xl" />
            <span className="font-medium">WhatsApp</span>
          </a>
          <a
            href={`tel:${whatsappNumber}`}
            className="flex items-center space-x-3 bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-all duration-300 hover:scale-105"
          >
            <FaPhoneAlt className="text-xl" />
            <span className="font-medium">Call Now</span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default CocopeatCoins;